import React, { Dispatch, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../../ui/toast';
import { Button } from '../../../../../../ui';
import { UploadFileComponent } from '../../../../../../ui/input/FileInput';
import { uploadFileJobDescription } from '../../../../../../utils/api_service/JobOpenings';

const fileTypes = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

type ImportJobDescriptionModalProps = {
  closeModal: () => void;
  setLoading: Dispatch<React.SetStateAction<boolean>>;
};

const ImportJobDescription = ({
  closeModal,
  setLoading,
}: ImportJobDescriptionModalProps) => {
  const { id } = useParams<{ id: string }>();
  const [uploadedFile, setUploadedFile] = useState<File[] | null>(null);
  const [transcript, setTranscript] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  const fileUploaded = uploadedFile ? uploadedFile.length > 0 : false;

  const uploadJobDescription = async () => {
    if (!uploadedFile?.length && !transcript) {
      toast.error('Please select a file or enter a transcript', {
        position: 'bottom-center',
      });
      return;
    }

    setIsImporting(true);

    try {
      const source =
        uploadedFile && uploadedFile?.length > 0 ? uploadedFile[0] : undefined;
      const response = await uploadFileJobDescription(id, source, transcript);
      if (response.ok) {
        toast.success(
          'Job description successfully uploaded and being processed...',
          {
            position: 'bottom-center',
          },
        );
        setLoading(true);
        closeModal();
      } else {
        setIsImporting(false);
        toast.error('Upload unsuccessful. Please try again.', {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      setIsImporting(false);
      toast.error('Error uploading job description. Please try again.', {
        position: 'bottom-center',
      });
    }
  };

  return (
    <div className='flex flex-col w-full max-w-3xl mx-auto'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        <div>
          <div className='flex items-center gap-2 mb-4'>
            <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-600'>
              1
            </div>
            <h3 className='text-base font-medium text-gray-700'>Upload File</h3>
          </div>
          <div className='bg-white rounded-lg border border-gray-200'>
            <UploadFileComponent
                uploadedFile={uploadedFile}
                setUploadedFile={setUploadedFile}
                validTypes={fileTypes}
                isMultiple={false}
                disabled={transcript !== ''}
            />
          </div>
        </div>

        <div>
          <div className='flex items-center gap-2 mb-4'>
            <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-600'>
              2
            </div>
            <h3 className='text-base font-medium text-gray-700'>
              Or Paste Description
            </h3>
          </div>
          <textarea
              className={`w-full h-[250px] p-4 rounded-lg bg-white text-gray-700 placeholder-gray-400 text-sm border border-gray-200 resize-none transition-colors duration-200 ${
                fileUploaded
                  ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                  : 'hover:border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
              }`}
              placeholder='Paste the Job Description here...'
              onChange={(e) => setTranscript(e.target.value)}
              disabled={fileUploaded}
          />
        </div>
      </div>

      <div className='flex flex-col items-center gap-3 mt-8'>
        <Button
            onClick={uploadJobDescription}
            variant={'primary'}
            label={isImporting ? 'Importing...' : 'Import Job Description'}
            disabled={!(fileUploaded || transcript !== '') || isImporting}
            icon={
              isImporting ? (
                <svg
                    className='animate-spin -ml-1 mr-2 h-4 w-4'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                >
                  <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                  ></circle>
                  <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
              ) : undefined
            }
            className='w-48'
        />
        { isImporting && (
          <p className='text-sm text-gray-500 animate-pulse'>
            Processing your job description...
          </p>
        ) }
      </div>
    </div>
  );
};

export default ImportJobDescription;
